import { h, FunctionComponent } from 'preact';
import { useSelector, useDispatch, batch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AddressInput as AddressInputComponent } from 'up-ops-web-shared-components';
import {
  AddressType,
  OptionType,
} from 'up-ops-web-shared-components/lib/components/inputs/address-input';
import { useState, useEffect } from 'preact/hooks';
import { ThemeProvider } from 'styled-components';
import * as startFormActions from '~src/domains/start/state/startFormReducer';

import { localeSelector } from '~src/domains/common/state/commonSelectors';
import { StateObject } from '~src/domains/common/types/common';

type AddressKeys = 'streetAddress' | 'city' | 'region' | 'postalCode';
type StateKeys = 'address' | 'city' | 'region' | 'postal_code';
type AddressInputProps = {
  getForm: (state: StateObject) => { [k: string]: any };
  formActions: any;
};

const AddressInput: FunctionComponent<AddressInputProps> = props => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { getForm, formActions } = props;
  // @ts-ignore
  const inputState = useSelector(getForm);
  const locale = useSelector(localeSelector);
  const [language, country] = locale.split('-');
  const regions =
    t('common:regions', {
      returnObjects: true,
    }) || {};
  const [regionsMap, setRegionsMap] = useState<OptionType[]>([]);
  const addressValues = {
    streetAddress: inputState.address.value,
    city: inputState.city.value,
    region: inputState.region.value,
    postalCode: inputState.postal_code.value,
  } as AddressType;

  const inputs = t('start:regular-application.inputs', {
    returnObjects: true,
  }) as startFormActions.StartLocalization;

  const addressOnChange = (newVal: AddressType) => {
    const keyMap: { [key in AddressKeys]: StateKeys } = {
      streetAddress: 'address',
      city: 'city',
      region: 'region',
      postalCode: 'postal_code',
    };

    batch(() => {
      Object.entries(newVal).forEach(([name, value]) => {
        if (value === undefined) return;

        if (value !== addressValues[name as AddressKeys]) {
          const stateName = keyMap[name as AddressKeys];
          dispatch(formActions.updateInputValue({ name: stateName, value }));
        }
      });
    });
  };

  const makeOptionsMap = (optionsMap: object, placeholder?: string) => {
    const options = [];
    if (placeholder) {
      options.push({
        value: '',
        label: placeholder,
      });
    }
    const keys = Object.keys(optionsMap);
    const len = keys.length;

    for (let i = 0; i < len; i++) {
      const key = keys[i] as keyof typeof optionsMap;
      const value = optionsMap[key];
      options.push({ value: key, label: value });
    }
    return options;
  };

  useEffect(() => {
    i18n.changeLanguage(locale);
    const newRegionsMap = makeOptionsMap(regions);
    if (inputs.region?.placeholder) {
      newRegionsMap.unshift({
        value: '',
        label: inputs.region.placeholder,
      });
    }
    setRegionsMap(newRegionsMap);
  }, [locale]);

  return (
    <div className='form-group' style={{ padding: '0' }}>
      <ThemeProvider
        theme={(outerTheme: any) => ({
          ...outerTheme,
          colors: {
            ...outerTheme.colors,
            error: '#b52518',
            borderDefault: '#8e919c',
          },
        })}
      >
        <AddressInputComponent
          addressValues={addressValues}
          streetAddressDisabled={inputState.address.isDisabled}
          cityDisabled={inputState.city.isDisabled}
          regionDisabled={inputState.region.isDisabled}
          postalCodeDisabled={inputState.postal_code.isDisabled}
          streetAddressLabel={
            inputState.address.isValid === false
              ? inputState.address.errorMessage
              : inputs.address?.label
          }
          cityLabel={
            inputState.city.isValid === false
              ? inputState.city.errorMessage
              : `${inputs.city?.label}`
          }
          regionLabel={
            inputState.region.isValid === false
              ? inputState.region.errorMessage
              : `${inputs.region?.label}`
          }
          postalCodeLabel={
            inputState.postal_code.isValid === false
              ? inputState.postal_code.errorMessage
              : `${inputs.postal_code?.label}`
          }
          streetAddressError={inputState.address.isValid === false}
          cityError={inputState.city.isValid === false}
          regionError={inputState.region.isValid === false}
          postalCodeError={inputState.postal_code.isValid === false}
          locale={language as 'en' | 'fr'}
          streetAddressPlaceholder={inputs.address?.placeholder || ''}
          cityPlaceholder={inputs.city?.placeholder || ''}
          postalCodePlaceholder={inputs.postal_code?.placeholder}
          postalCodeFormat={inputs.postal_code?.mask}
          regionOptions={regionsMap}
          onChange={addressOnChange}
          countries={[country.toLowerCase()]}
          autocomplete={true}
          onAutocompleteOptionClick={(addressComponents: any) => {
            let streetAddress = '';
            if (addressComponents) {
              for (const component of addressComponents) {
                // @ts-ignore
                const componentType = component?.types[0];

                switch (componentType) {
                  case 'street_number': {
                    // @ts-ignore
                    streetAddress = `${component.long_name} ${streetAddress}`;
                    break;
                  }

                  case 'route': {
                    // @ts-ignore
                    streetAddress += component.short_name;
                    break;
                  }
                }
              }
              dispatch(
                formActions.updateInputValue({
                  name: 'address',
                  value: streetAddress,
                }),
              );
            }
          }}
        />
      </ThemeProvider>
    </div>
  );
};

export default AddressInput;
